/**
 * LERA Premium Popover — Sprint 8.3B.2
 * Reusable LCS popover engine (delivery is first consumer).
 *
 * Countdown dispatches lera:popover:toggle — this runtime owns open/close/position/focus.
 */

const LERA_POPOVER_MOBILE_BP = 750;
const LERA_POPOVER_ANIM_MS = 200;
const LERA_POPOVER_VIEWPORT_PAD = 12;

const LERA_POPOVER_STYLE_VARS = [
  '--lera-popover-width-desktop',
  '--lera-popover-width-mobile',
  '--lera-popover-padding-desktop',
  '--lera-popover-padding-mobile',
  '--lera-popover-radius-desktop',
  '--lera-popover-radius-mobile',
  '--lera-popover-title-desktop',
  '--lera-popover-title-mobile',
  '--lera-popover-body-desktop',
  '--lera-popover-body-mobile',
  '--lera-popover-gap-desktop',
  '--lera-popover-gap-mobile',
  '--lera-popover-shadow',
];

function leraPopoverIsMobile() {
  return window.matchMedia(`(max-width: ${LERA_POPOVER_MOBILE_BP - 1}px)`).matches;
}

function leraPopoverGetPanelWidth(panelEl, viewportWidth) {
  const measured = panelEl.getBoundingClientRect().width;
  if (measured > 40) return measured;

  const styles = getComputedStyle(panelEl);
  const desktop = parseFloat(styles.getPropertyValue('--lera-popover-width-desktop')) || 360;
  if (leraPopoverIsMobile()) {
    const mobileVw = parseFloat(styles.getPropertyValue('--lera-popover-width-mobile')) || 90;
    return Math.min((viewportWidth * mobileVw) / 100, viewportWidth - 24);
  }
  return Math.min(desktop, viewportWidth - 24);
}

function leraPopoverSetExpanded(trigger, expanded) {
  if (!trigger) return;
  trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
}

if (!customElements.get('lera-premium-popover')) {
  customElements.define(
    'lera-premium-popover',
    class LeraPremiumPopover extends HTMLElement {
      constructor() {
        super();
        this.panelEl = null;
        this.panelPlaceholder = null;
        this.panelPortaled = false;
        this.activeTrigger = null;
        this.isOpen = false;
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
        this.onResize = this.onResize.bind(this);
        this.toggleListener = this.onToggleEvent.bind(this);
      }

      connectedCallback() {
        this.panelEl = this.querySelector('[data-lera-popover-panel]');
        document.addEventListener('lera:popover:toggle', this.toggleListener);
      }

      disconnectedCallback() {
        document.removeEventListener('lera:popover:toggle', this.toggleListener);
        this.close(false);
      }

      onToggleEvent(event) {
        if (!event?.detail || event.detail.popoverId !== this.id) return;
        this.toggle(event.detail.trigger);
      }

      getTriggers() {
        return document.querySelectorAll(`[data-lera-popover-trigger][aria-controls="${this.id}"]`);
      }

      syncPanelThemeVars() {
        if (!this.panelEl) return;
        const hostStyles = getComputedStyle(this);
        LERA_POPOVER_STYLE_VARS.forEach((name) => {
          const value = hostStyles.getPropertyValue(name);
          if (value) {
            this.panelEl.style.setProperty(name, value.trim());
          }
        });
      }

      mountPanel() {
        if (!this.panelEl || this.panelPortaled) return;
        this.panelPlaceholder = document.createComment('lera-popover-panel-anchor');
        this.panelEl.before(this.panelPlaceholder);
        this.syncPanelThemeVars();
        document.body.appendChild(this.panelEl);
        this.panelPortaled = true;
      }

      unmountPanel() {
        if (!this.panelEl || !this.panelPortaled || !this.panelPlaceholder?.parentNode) return;
        this.panelPlaceholder.parentNode.insertBefore(this.panelEl, this.panelPlaceholder);
        this.panelPlaceholder.remove();
        this.panelPlaceholder = null;
        this.panelPortaled = false;
      }

      toggle(trigger) {
        if (this.isOpen && this.activeTrigger === trigger) {
          this.close(true);
          return;
        }
        this.open(trigger);
      }

      open(trigger) {
        if (!this.panelEl || !trigger) return;

        this.activeTrigger = trigger;
        this.hidden = false;
        this.mountPanel();
        this.classList.add('is-opening');
        this.panelEl.classList.add('is-opening');
        this.panelEl.setAttribute('aria-hidden', 'false');
        leraPopoverSetExpanded(trigger, true);
        this.getTriggers().forEach((node) => {
          if (node !== trigger) leraPopoverSetExpanded(node, false);
        });

        this.positionPanel(trigger);

        requestAnimationFrame(() => {
          this.positionPanel(trigger);
          this.classList.remove('is-opening');
          this.classList.add('is-open');
          this.panelEl.classList.remove('is-opening');
          this.panelEl.classList.add('is-open');
          this.isOpen = true;
          this.panelEl.focus({ preventScroll: true });
        });

        document.addEventListener('click', this.onDocumentClick, true);
        document.addEventListener('keydown', this.onDocumentKeydown, true);
        window.addEventListener('resize', this.onResize, { passive: true });
      }

      close(restoreFocus = true) {
        if (!this.isOpen && this.hidden) return;

        this.classList.remove('is-open');
        this.panelEl?.classList.remove('is-open');
        this.isOpen = false;
        this.panelEl?.setAttribute('aria-hidden', 'true');
        this.getTriggers().forEach((node) => leraPopoverSetExpanded(node, false));

        window.setTimeout(() => {
          if (this.isOpen) return;
          this.hidden = true;
          this.classList.remove('is-opening');
          this.panelEl?.classList.remove('is-opening');
          this.panelEl?.removeAttribute('style');
          LERA_POPOVER_STYLE_VARS.forEach((name) => this.panelEl?.style.removeProperty(name));
          this.unmountPanel();
        }, LERA_POPOVER_ANIM_MS);

        document.removeEventListener('click', this.onDocumentClick, true);
        document.removeEventListener('keydown', this.onDocumentKeydown, true);
        window.removeEventListener('resize', this.onResize);

        if (restoreFocus && this.activeTrigger) {
          this.activeTrigger.focus({ preventScroll: true });
        }
        this.activeTrigger = null;
      }

      onDocumentClick(event) {
        if (!this.isOpen) return;
        const target = event.target;
        if (this.panelEl?.contains(target)) return;
        if (this.activeTrigger?.contains(target)) return;
        if (target?.closest?.(`[data-lera-popover-trigger][aria-controls="${this.id}"]`)) return;
        this.close(true);
      }

      onDocumentKeydown(event) {
        if (!this.isOpen) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close(true);
        }
      }

      onResize() {
        if (this.isOpen && this.activeTrigger) {
          this.positionPanel(this.activeTrigger);
        }
      }

      positionPanel(trigger) {
        if (!this.panelEl) return;

        const triggerRect = trigger.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const isMobile = leraPopoverIsMobile();

        this.panelEl.style.position = 'fixed';
        this.panelEl.style.margin = '0';
        this.panelEl.style.zIndex = '30';
        this.panelEl.style.maxHeight = `${viewportHeight - LERA_POPOVER_VIEWPORT_PAD * 2}px`;
        void this.panelEl.offsetHeight;

        const panelRect = this.panelEl.getBoundingClientRect();
        const panelHeight = panelRect.height;
        const panelWidth = leraPopoverGetPanelWidth(this.panelEl, viewportWidth);

        if (isMobile) {
          const maxWidth = Math.min(panelWidth || viewportWidth * 0.9, viewportWidth * 0.9);
          const left = Math.max(LERA_POPOVER_VIEWPORT_PAD, (viewportWidth - maxWidth) / 2);
          let top = triggerRect.bottom + 8;
          if (top + panelHeight > viewportHeight - LERA_POPOVER_VIEWPORT_PAD) {
            top = Math.max(LERA_POPOVER_VIEWPORT_PAD, triggerRect.top - panelHeight - 8);
          }
          top = Math.min(
            top,
            Math.max(LERA_POPOVER_VIEWPORT_PAD, viewportHeight - panelHeight - LERA_POPOVER_VIEWPORT_PAD)
          );
          this.panelEl.style.width = `${maxWidth}px`;
          this.panelEl.style.left = `${left}px`;
          this.panelEl.style.top = `${top}px`;
          this.panelEl.style.transformOrigin = 'top center';
          return;
        }

        this.panelEl.style.width = '';
        let left = triggerRect.left;
        let top = triggerRect.bottom + 8;

        if (left + panelWidth > viewportWidth - LERA_POPOVER_VIEWPORT_PAD) {
          left = triggerRect.right - panelWidth;
        }

        left = Math.min(left, viewportWidth - panelWidth - LERA_POPOVER_VIEWPORT_PAD);
        left = Math.max(left, LERA_POPOVER_VIEWPORT_PAD);

        if (top + panelHeight > viewportHeight - LERA_POPOVER_VIEWPORT_PAD) {
          top = triggerRect.top - panelHeight - 8;
        }
        top = Math.min(
          top,
          Math.max(LERA_POPOVER_VIEWPORT_PAD, viewportHeight - panelHeight - LERA_POPOVER_VIEWPORT_PAD)
        );
        if (top < LERA_POPOVER_VIEWPORT_PAD) {
          top = LERA_POPOVER_VIEWPORT_PAD;
        }

        this.panelEl.style.left = `${left}px`;
        this.panelEl.style.top = `${top}px`;
        this.panelEl.style.transformOrigin = 'top left';
      }
    }
  );
}
